import { HostedFieldsTokenizePayload } from 'braintree-web';

import { NutsJson, webstore } from '@/api';
import { NutsAddress } from '@/utils/address';

export interface TokenizedPaymentMethod extends HostedFieldsTokenizePayload {
  billingAddress: NutsAddress;
  /**
   * Indicates if payment method is user's default
   */
  default?: boolean;
  setAsDefault?: boolean;
}

interface PaymentMethodRequest {
  customerId: string;
  paymentMethodId?: number;
}

interface AddPaymentMethodRequest extends Omit<PaymentMethodRequest, 'paymentMethodId'> {
  paymentMethod: TokenizedPaymentMethod;
}

interface VerifyUSBankAccountRequest extends PaymentMethodRequest {
  braintreeId?: string;
  deviceData?: string;
  setAsDefault?: boolean;
  synthesizedPaymentMethod: {
    nonce: string;
    details: {
      cardType: string;
      cardholderName: string;
      expirationYear: string;
      expirationMonth: string;
      lastFour: string;
    };
    billingAddress: {
      company?: string;
      countryCodeAlpha2?: string;
      countryCodeAlpha3?: string;
      countryCodeNumeric?: string;
      countryName?: string;
      extendedAddress?: string;
      firstName?: string;
      lastName?: string;
      locality?: string;
      postalCode?: string;
      region?: string;
      streetAddress?: string;
    };
  };
}

export interface VerifyUSBankAccountResponse {
  lastFourDigits: string;
  paymentMethodId?: number;
  status: string;
}

interface ConfirmUSBankAccountRequest extends PaymentMethodRequest {
  amounts: number[];
  deviceData?: string;
  paymentMethodId: number;
}

interface ConfirmUSBankAccountResponse {
  paymentMethodId?: number;
  status: string;
  error?: string;
}

interface DeletePaymentMethodResponse {
  paymentMethodId: number;
}

export async function addBraintreePaymentMethodToWallet(request: AddPaymentMethodRequest) {
  const { data } = await webstore.sessionSpecific.post<
    NutsJson<{ paymentMethod: TokenizedPaymentMethod }>
  >('/api/payment-methods/braintree', request);
  return data;
}

export async function verifyUSBankAccount(request: VerifyUSBankAccountRequest) {
  const { data } = await webstore.sessionSpecific.post<NutsJson<VerifyUSBankAccountResponse>>(
    '/api/payment-methods/verify',
    request,
  );
  return data;
}

export async function confirmUSBankAccount(request: ConfirmUSBankAccountRequest) {
  const { data } = await webstore.sessionSpecific.post<NutsJson<ConfirmUSBankAccountResponse>>(
    '/api/payment-methods/confirm',
    request,
  );
  return data;
}

export async function defaultPaymentMethod(request: PaymentMethodRequest) {
  return webstore.sessionSpecific.post('/api/payment-methods/default', request);
}

export async function deletePaymentMethod(request: PaymentMethodRequest) {
  const { data } = await webstore.sessionSpecific.post<NutsJson<DeletePaymentMethodResponse>>(
    '/api/payment-methods/delete',
    request,
  );
  return data;
}
